<template>
  <div>
    <!-- `dark` fixo: o shell de navegação é escuro nos dois temas, com topbar e
         conteúdo em superfície clara. É o ritmo claro/escuro da marca — ver
         design-system/README.md e a referência product/nav do projeto de design.
         O `dark` também vira os tokens --tc-* para a variante escura aqui dentro. -->
    <v-navigation-drawer
      v-if="showDrawer"
      v-model="drawer"
      app
      dark
      :mini-variant="modoTrilho"
      :expand-on-hover="modoTrilho"
      :width="320"
      :mini-variant-width="72"
      mobile-breakpoint="960"
    >
      <!-- Logo no topo da sidebar -->
      <div class="sidebar-logo">
        <LogoType
          on-dark
          class="sidebar-logo-full"
        />
        <LogoType
          :use-symbol="true"
          on-dark
          class="sidebar-logo-mini"
        />
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              x-small
              class="sidebar-pin-btn"
              :aria-label="pinLabel"
              v-bind="attrs"
              v-on="on"
              @click="togglePin"
            >
              <PhPushPin
                :size="16"
                :color="isPinned ? 'var(--tc-shell-accent)' : 'var(--tc-shell-text)'"
                :weight="isPinned ? 'fill' : 'regular'"
              />
            </v-btn>
          </template>
          <span>{{ pinLabel }}</span>
        </v-tooltip>
      </div>
      <MenuStaticCustomerAccess v-if="appData.customerAccessMode" />
      <MenuStatic v-else />
    </v-navigation-drawer>
    <!-- Header enxuto: sem faixa branca e sem borda — flui direto no fundo da
         página. A separação do conteúdo passa a ser o respiro, não uma régua.
         A busca começa recolhida em ícone; o campo permanente ocupava um terço
         da barra sem estar em uso na maior parte do tempo. -->
    <v-app-bar
      v-if="showDrawer"
      flat
      height="64"
      color="transparent"
      class="tc-topbar"
      :style="appBarStyle"
    >
      <v-app-bar-nav-icon
        class="d-md-none"
        @click="drawer = !drawer"
      />
      <!-- Só no desktop: no celular a barra já tem o menu e as ações, e o nome
           da página aparece no cabeçalho da própria tela. -->
      <BreadcrumbNav class="d-none d-md-flex" />
      <v-spacer />

      <!-- A direita é lida em três grupos, separados por hairline: o que
           CONSULTA (busca e avisos), o que EXECUTA (simulação) e QUEM é o
           usuário. Antes eram sete controles em fila única, sem hierarquia. -->
      <div class="tc-topbar__group">
        <!-- Clara: só o gatilho fica aqui. O painel é montado em App.vue,
             fora deste componente, que é remontado por `:key`. Quem decide se
             o lançador aparece é o próprio ClaraLauncher — a regra de perfil
             mora em `claraAccess`. -->
        <ClaraLauncher v-access="'AI_ASSISTANT'" />

        <LanguageSelector />

        <!-- WhatsApp badge -->
        <v-tooltip
          v-if="hasWhatsAppAccess"
          bottom
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              @click="goToWhatsAppInbox"
            >
              <div class="whatsapp-icon-container">
                <PhChatCircleDots :size="18" />
                <span
                  v-if="whatsappUnreadCount > 0"
                  class="whatsapp-notification-badge"
                >
                  {{ whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount }}
                </span>
              </div>
            </v-btn>
          </template>
          <span>{{ whatsappTooltipText }}</span>
        </v-tooltip>

        <NotificationPanel />

        <!-- Notas de versao falam de telas e recursos da operacao interna.
             Quem esta na borda nao tem contexto para elas — e parte do que
             sai ali e roadmap que nao precisa atravessar. -->
        <v-tooltip
          v-if="showReleasesIcon && isPerfilAdministrativo"
          bottom
        >
          <template #activator="{ on }">
            <v-btn
              icon
              v-on="on"
              @click="openReleasesModal"
            >
              <div class="releases-icon-container">
                <PhMegaphone :size="18" />
                <span
                  v-if="hasRecentRelease"
                  class="releases-badge"
                >
                  <span class="releases-badge-dot"></span>
                </span>
              </div>
            </v-btn>
          </template>
          <span>{{ $t('menu.topbar.latestUpdates') }}</span>
        </v-tooltip>
      </div>

      <span class="tc-topbar__sep"></span>

      <!-- `secondary` (contorno) e não `tonal` (preenchido): sem a faixa branca,
           um bloco tingido no meio de ícones limpos vira o elemento mais pesado
           do header sem ser a ação mais importante. -->
      <div class="tc-topbar__group">
        <div
          v-if="showPreSaleNotification"
          class="pre-sale-notification-content"
        >
          <PhWarning
            :size="16"
            class="mr-2"
          />
          <span>{{ preSaleText }}</span>
        </div>

        <!-- Copia o link fixo de cadastro: o do corban (para indicar um cliente)
             ou, para o admin, o da empresa (cliente sem corban, para o site). -->
        <Button
          v-if="canShareSignupLink"
          variant="secondary"
          class="d-none d-sm-flex"
          :loading="copyingSignupLink"
          data-test="btn-header-link-cadastro"
          @click="copySignupLink"
        >
          <PhLink
            class="mr-2"
            :size="18"
          />
          {{ signupLinkButtonLabel }}
        </Button>
        <v-btn
          v-if="canShareSignupLink"
          icon
          class="d-flex d-sm-none"
          :aria-label="signupLinkButtonLabel"
          @click="copySignupLink"
        >
          <PhLink :size="18" />
        </v-btn>
        <Button
          v-if="!isOpenSimulator && !appData.customerAccessMode"
          variant="secondary"
          class="d-none d-sm-flex"
          @click="openOperationSimulator"
        >
          <PhCurrencyCircleDollar
            class="mr-2"
            :size="18"
          />
          {{ $t('menu.simulate.open') }}
        </Button>
        <v-chip
          v-if="isOpenSimulator && !appData.customerAccessMode"
          class="text-truncate"
          close
          @click:close="closeOperationSimulator"
        >
          <span class="pr-2">{{ $t('menu.simulate.active') }}</span>
        </v-chip>
        <!-- No mobile a mesma ação vira ícone; sem `color="primary"` para não
             reintroduzir o círculo royal entre os ícones neutros. -->
        <v-btn
          v-if="!isOpenSimulator && !appData.customerAccessMode"
          icon
          class="d-flex d-sm-none"
          @click="openOperationSimulator"
        >
          <PhCurrencyCircleDollar :size="18" />
        </v-btn>
      </div>

      <span class="tc-topbar__sep"></span>

      <!-- Usuário: o bloco inteiro abre o menu. Antes só a engrenagem ao lado
           era clicável — alvo pequeno, e nada indicava que o nome fazia parte
           do mesmo controle. -->
      <!-- `min-width` no MENU, nao `width` na lista. Sem ele o Vuetify usa a
           largura do ATIVADOR como minimo do dropdown (calculatedMinWidth), e o
           botao do usuario e largo porque carrega nome e e-mail: o menu abria com
           237px enquanto a `v-list` estava presa em 200px, sobrando uma faixa
           vazia de 37px a direita dos itens. Declarando o minimo aqui o menu
           passa a ser dimensionado pelo proprio conteudo, e a lista o preenche. -->
      <v-menu
        offset-y
        min-width="200"
      >
        <template #activator="{ on: menu, attrs }">
          <button
            v-bind="attrs"
            type="button"
            class="tc-topbar__user"
            data-test="btn-menu-usuario"
            v-on="{ ...menu }"
          >
            <!-- Sem `color="primary"`: preenchido de royal, o avatar virava o
                 elemento mais saturado do header inteiro — puxando o olho para
                 o controle menos importante da barra. Vira selo tingido. -->
            <v-avatar
              size="28"
              class="tc-selo tc-topbar__avatar"
            >
              <v-img
                v-if="showAvatarImage"
                :src="avatarUrl"
                @error="onAvatarError"
              />
              <span
                v-else-if="userInitials"
                class="tc-topbar__initials"
              >{{ userInitials }}</span>
              <PhUserCircle
                v-else
                :size="26"
              />
            </v-avatar>
            <span class="tc-topbar__identity d-none d-lg-flex">
              <span class="tc-topbar__name">{{ user.name }}</span>
              <span class="tc-topbar__mail">{{ user.email }}</span>
            </span>
            <PhCaretDown
              :size="12"
              class="tc-topbar__caret"
            />
          </button>
        </template>
        <v-list>
          <v-list-item-group>
            <v-list-item
              v-if="!appData.customerAccessMode"
              @click="() => (isOpenCalculationMemory = true)"
            >
              <PhInfo
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.calculationMemory') }}
            </v-list-item>
            <v-list-item @click="changeTheme">
              <PhSun
                v-if="$vuetify.theme.dark"
                :size="20"
                class="mr-2"
              />
              <PhMoon
                v-else
                :size="20"
                class="mr-2"
              />
              {{ $t('menu.userMenu.changeTheme') }}
            </v-list-item>
            <v-list-item
              v-if="!appData.customerAccessMode"
              v-access="$access.MENU.CONFIGURACOES"
              @click="goToConfigurations"
            >
              <PhWrench
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.settings') }}
            </v-list-item>
            <v-list-item
              v-if="!appData.customerAccessMode"
              v-access="$access.MENU.EMAILS_ENVIADOS"
              @click="goToEmailLogs"
            >
              <PhEnvelope
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.emailsSent') }}
            </v-list-item>
            <v-list-item
              v-access="$access.MENU.ALTERAR_SENHA"
              @click="changePassword()"
            >
              <PhLock
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.changePassword') }}
            </v-list-item>
            <v-list-item
              data-test="menu-formas-de-acesso"
              @click="openIdentities()"
            >
              <PhKey
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.accessMethods') }}
            </v-list-item>
            <v-list-item @click="openAvatarDialog">
              <PhUserCircle
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.changePhoto') }}
            </v-list-item>
            <v-list-item
              v-if="isAdminUser"
              @click="goToFirstSteps"
            >
              <PhRocket
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.firstSteps') }}
            </v-list-item>
            <v-list-item
              v-if="isPerfilAdministrativo"
              data-test="btn-suporte"
              @click="goToHelpDesk"
            >
              <PhQuestion
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.support') }}
            </v-list-item>
            <v-list-item
              data-test="btn-sair"
              @click="logout()"
            >
              <PhSignOut
                class="mr-2"
                :size="20"
              />
              {{ $t('menu.userMenu.logout') }}
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
    </v-app-bar>

    <UserIdentitiesModal v-model="identitiesWindow" />

    <BaseModal
      v-model="changePasswordWindow"
      :title="$t('menu.changePassword.title')"
      modal-width="420px"
      modal-height="auto"
      max-height-body="auto"
      :primary-button-action="updatePassword"
      :secondary-button-action="close"
    >
      <v-form ref="formSenha">
        <PasswordField
          :label="$t('menu.changePassword.currentPasswordLabel')"
          :rules="currentPasswordRules"
          autocomplete="new-password"
          autofocus
          required
          v-model="user.currentPassword"
        />
        <PasswordField
          :label="$t('menu.changePassword.newPasswordLabel')"
          :rules="newPasswordRules"
          autocomplete="new-password"
          required
          v-model="user.newPassword"
        />
        <PasswordField
          :label="$t('menu.changePassword.confirmPasswordLabel')"
          :rules="confirmPasswordRules"
          autocomplete="new-password"
          required
          v-model="user.confirmNewPassword"
        />
      </v-form>
    </BaseModal>

    <ReleasesModal :open.sync="isOpenReleasesModal" />

    <AvatarDialog
      v-if="isAvatarDialogOpen"
      v-model="isAvatarDialogOpen"
      :user-id="user.id"
      :initials="userInitials"
      :current-avatar-url="avatarObjectUrl"
      @avatar-updated="loadAvatar"
    />

    <v-dialog
      v-model="isOpenCalculationMemory"
      max-width="50%"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('menu.calculationMemory.title') }}</span>
        </v-card-title>

        <div class="alignment-variables">
          <ul>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.grossProfit.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.grossProfit.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.grossProfit.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.corban.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.corban.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.corban.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.corbanIndicator.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.corbanIndicator.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.corbanIndicator.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.operationTotal.term') }}:</strong>
              <ul>
                <li>
                  <strong>{{ $t('menu.calculationMemory.operationTotal.forRemittance') }}:</strong>
                  <span>{{ $t('menu.calculationMemory.operationTotal.forRemittanceDesc') }}</span>
                  <br />
                  <code>
                    <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                    {{ $t('menu.calculationMemory.operationTotal.forRemittanceExample') }}
                  </code>
                </li>
                <li>
                  <strong>{{ $t('menu.calculationMemory.operationTotal.forExchange') }}:</strong>
                  <span>{{ $t('menu.calculationMemory.operationTotal.forExchangeDesc') }}</span>
                  <br />
                  <code>
                    <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                    {{ $t('menu.calculationMemory.operationTotal.forExchangeExample') }}
                  </code>
                </li>
              </ul>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.pisCofins.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.pisCofins.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.pisCofins.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.contractCashback.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.contractCashback.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.contractCashback.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.bankProfit.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.bankProfit.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.bankProfit.example') }}
              </code>
            </li>
            <li class="pb-4">
              <strong>{{ $t('menu.calculationMemory.masterCorbanProfit.term') }}:</strong>
              <span>{{ $t('menu.calculationMemory.masterCorbanProfit.desc') }}</span>
              <br />
              <code>
                <strong>{{ $t('menu.calculationMemory.exampleLabel') }}:</strong>
                {{ $t('menu.calculationMemory.masterCorbanProfit.example') }}
              </code>
            </li>
          </ul>
        </div>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            class="btn-primary mr-2"
            @click="() => (isOpenCalculationMemory = false)"
          >
            {{ $t('menu.calculationMemory.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
// Api
import authApi from '@/api/auth/auth-api';
import ConfigurationApi from '@/api/generic/configuration-api';
import releasesApi from '@/api/general/releases-api';
import UserAvatarApi from '@/api/user/user-avatar-api';
import WhatsAppChatApi from '@/api/whatsapp/whatsapp-chat-api';

// Models
import UserModel from '@/model/user-model';
import ConfigurationModel from '@/model/generic/configuration-model';

// Components
import MenuStatic from '@/components/menu/MenuStatic.vue';
import MenuStaticCustomerAccess from '@/components/menu/MenuStaticCustomerAccess.vue';
import NotificationPanel from '@/components/menu/NotificationPanel.vue';
import ClaraLauncher from '@/views/clara/ClaraLauncher.vue';
import { OPTIONS_STORE_CLARA } from '@/vuex/module/clara';
import { OPTIONS_STORE_SUPPORT_WIDGET } from '@/vuex/module/supportWidget';
import LogoType from '@/components/comum/LogoType.vue';
import ReleasesModal from '@/components/comum/ReleasesModal.vue';
import BreadcrumbNav from '@/components/menu/BreadcrumbNav.vue';
import AvatarDialog from '@/components/menu/AvatarDialog.vue';
import UserIdentitiesModal from '@/components/auth/identity/UserIdentitiesModal.vue';
import Button from '@/components/comum/Button.vue';
import BaseModal from '@/components/comum/BaseModal.vue';
import PasswordField from '@/components/comum/PasswordField.vue';
import LanguageSelector from '@/components/menu/LanguageSelector.vue';


// Utils
import utilsStorage from '@/utils/storage';

// Mixin
import mixinMessage from '@/mixin/mixin-message';
import mixinGeral from '@/mixin/mixin-geral';
import mixinUserAvatar from '@/mixin/mixin-user-avatar';
import mixinOnboardingChecklist from '@/mixin/mixin-onboarding-checklist';
import mixinCorbanSignupLink from '@/mixin/mixin-corban-signup-link';

// Tracking
import mixpanel from 'mixpanel-browser';

import { OPTIONS_STORE_CONFIGURATIONS } from '@/vuex/module/configurations';
import { OPTIONS_STORE_RELEASES } from '@/vuex/module/releases';
import { isAdministrativeProfile } from '@/utils/profile';
import { OPTIONS_STORE_ACCESS } from '@/vuex/module/access';
import { ACCESS_VALUES } from '../../../../common/constants/global';
import { SUPPORT_ENTRY_PATH } from '@/constants/route-constants';
import { mapGetters } from 'vuex';

const SUPPORT_BUTTON_NAME = { WIDGET: 'btn_support_widget', PORTAL: 'btn_help_desk' };

export default {
  name: 'Menu',
  mixins: [mixinMessage, mixinGeral, mixinUserAvatar, mixinOnboardingChecklist, mixinCorbanSignupLink],
  components: {
    MenuStatic,
    MenuStaticCustomerAccess,
    NotificationPanel,
    ClaraLauncher,
    LogoType,
    ReleasesModal,
    BreadcrumbNav,
    AvatarDialog,
    Button,
    BaseModal,
    PasswordField,
    UserIdentitiesModal,
    LanguageSelector
  },
  inject: ['appData'],
  data() {
    return {
      drawer: !this.appData.isMobile,
      isPinned: localStorage.getItem('menu-pinned') === 'true',
      routes: [],
      isOpenCalculationMemory: false,
      changePasswordWindow: false,
      identitiesWindow: false,
      user: new UserModel(),
      email: '',
      configurationApi: new ConfigurationApi(this.appData.currentUser),
      userAvatarApi: new UserAvatarApi(this.appData.currentUser),
      whatsAppChatApi: new WhatsAppChatApi(this.appData.currentUser),
      // WhatsApp unread count
      whatsappUnreadCount: 0,
      // Dados para notificação de pré-venda
      preSaleOperationsCount: 0,
      showPreSaleNotification: false,
      isOpenReleasesModal: false,
      isAvatarDialogOpen: false,
      isAvatarError: false,
      avatarObjectUrl: null
    };
  },
  mounted() {
    const user = utilsStorage.getUserOnStorage();

    if (!user) return;

    this.user = new UserModel(user);
    this.startPendo();
    this.deferredLoadConfigurations();
    this.loadAvatar();

    // Carregar WhatsApp unread count do localStorage
    this.loadWhatsAppUnreadCount();

    this.checkRecentReleases();

    // Escutar eventos de WhatsApp
    this.$eventBus.$on('whatsapp:unread-refresh', this.onWhatsAppUnreadRefresh);

    this._initOnboardingState();
  },

  beforeDestroy() {
    if (this._configurationsTimeoutId) {
      clearTimeout(this._configurationsTimeoutId);
    }

    this.$eventBus.$off('whatsapp:unread-refresh', this.onWhatsAppUnreadRefresh);
  },
  computed: {
    signupLinkButtonLabel() {
      return this.isCorbanUser ? this.$t('menu.signupLink.referClient') : this.$t('menu.signupLink.signupLink');
    },
    ...mapGetters({
      showReleasesIcon: OPTIONS_STORE_RELEASES.GETTERS.SHOW_RELEASES_ICON,
      hasRecentRelease: OPTIONS_STORE_RELEASES.GETTERS.HAS_RECENT_RELEASE
    }),

    isPerfilAdministrativo() {
      return isAdministrativeProfile(this.appData?.currentUser);
    },

    showDrawer() {
      return this.$route?.name !== 'DuimpExternalAccess';
    },

    /* Modo trilho: sidebar recolhida em 72px que se abre POR CIMA do conteudo no
       hover, sem empurrar nada. Quem entrega isso e o `expand-on-hover` do
       Vuetify: ele decide a largura VISUAL por `isMouseover`, mas reserva espaco
       no layout pela prop `mini-variant`, que aqui fica fixa em true. Antes a
       prop era sincronizada com o hover, entao os 320px viravam padding do
       v-main e a tela inteira deslizava a cada passada de mouse.

       Fixado (pin) e o oposto: a sidebar fica aberta e o conteudo cede espaco. */
    modoTrilho() {
      return !this.appData.isMobile && !this.isPinned;
    },

    /* O alfinete e o unico jeito de sair do modo que empurra o conteudo, e sem
       rotulo ninguem descobre que ele faz isso. */
    whatsappTooltipText() {
      return this.whatsappUnreadCount > 0
        ? this.$t('menu.topbar.whatsappUnread', { count: this.whatsappUnreadCount })
        : this.$t('menu.topbar.whatsapp');
    },

    preSaleText() {
      return this.$tc('menu.preSale.openOperations', this.preSaleOperationsCount, { count: this.preSaleOperationsCount });
    },

    pinLabel() {
      return this.isPinned
        ? this.$t('menu.topbar.unpinSidebar')
        : this.$t('menu.topbar.pinSidebar');
    },

    appBarStyle() {
      if (!this.showDrawer) return { marginLeft: '0' };

      return {
        // Acompanha o espaco RESERVADO, nao a largura visual: no modo trilho o
        // header fica parado em 72px enquanto a sidebar passa por cima dele.
        marginLeft: this.appData.isMobile ? '0' : this.modoTrilho ? '72px' : '320px',
        // A Clara é fixa e cobriria a direita do header — inclusive o próprio
        // botão que a fecha. O header recua junto com o conteúdo.
        marginRight: this.claraOffset ? `${this.claraOffset}px` : '0'
      };
    },

    /** Largura ocupada pela Clara; 0 quando fechada ou em tela pequena. */
    claraOffset() {
      if (this.$vuetify?.breakpoint?.smAndDown) return 0;

      return this.$store.getters[OPTIONS_STORE_CLARA.GETTERS.CONTENT_OFFSET];
    },


    /**
     * Verifica se o usuario tem acesso ao WhatsApp Inbox.
     * Feature desabilitada — WhatsApp removido do sistema.
     */
    hasWhatsAppAccess() {
      return false;
    },

    /**
     * Iniciais do nome do usuário para exibir no avatar.
     * Retorna as iniciais (primeira letra do primeiro nome + primeira letra do último nome).
     * Retorna string vazia se dados do usuário estiverem ausentes.
     */
    userInitials() {
      const name = this.user && this.user.name;

      if (!name || !name.trim()) return '';

      const parts = name.trim().split(/\s+/).filter(Boolean);

      if (parts.length === 0) return '';

      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    avatarUrl() {
      return this.avatarObjectUrl;
    },

    showAvatarImage() {
      return !!this.avatarUrl && !this.isAvatarError;
    },

    /**
     * Nome do cliente para exibir ao lado do avatar no modo customer access.
     */
    customerDisplayName() {
      if (!this.appData.customerAccessMode) return '';

      const name = this.appData.currentUser && this.appData.currentUser.name;

      return name || '';
    },


    menuSections() {
      return [
        {
          label: 'PRINCIPAL',
          items: [
            { title: 'Home', icon: 'PhHouse', to: '/' },
            { title: 'Dashboard', icon: 'PhChartPie', to: '/dashboard', access: [this.$access.MENU.DASHBOARD] }
          ]
        },
        {
          label: 'OPERACIONAL',
          items: [
            { title: 'Clientes', icon: 'PhUsers', to: '/customers', access: [this.$access.MENU.CLIENTES] },
            { title: 'Fluxo cadastral', icon: 'PhClipboard', to: '/customers-authorizations', access: [this.$access.MENU.FLUXO_CADASTRAL] },
            { title: 'Operações', icon: 'PhCurrencyCircleDollar', to: '/operations', access: [this.$access.MENU.OPERACOES] },
            { title: 'Pré-venda', icon: 'PhMoney', to: '/reports/operations/open', access: [this.$access.MENU.RASCUNHO_OPERACOES] }
          ]
        },
        {
          label: 'RELATÓRIOS',
          items: [
            {
              title: 'Relatórios operacionais',
              icon: 'PhFileText',
              access: [
                this.$access.MENU.RELATORIOS_OPERACIONAIS.REMESSAS_INTERNACIONAIS,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.LUCRO_REMESSAS_INTERNACIONAIS,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.CONTA_CORRENTE_BANCOS,
                this.$access.MENU.RELATORIOS_OPERACIONAIS.CAMBIO_TURISMO
              ],
              children: [
                { title: 'Remessas internacionais', to: '/reports/remittance', access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.REMESSAS_INTERNACIONAIS] },
                { title: 'Lucro / Remessas internacionais', to: '/reports/remittance/management', access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.LUCRO_REMESSAS_INTERNACIONAIS] },
                { title: 'Câmbio turismo', to: '/reports/exchange', access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.CAMBIO_TURISMO] }
              ]
            },
            {
              title: 'Relatórios Stablecoin',
              icon: 'PhCurrencyBtc',
              access: [
                this.$access?.STABLECOIN?.RELATORIOS?.REMESSAS,
                this.$access?.STABLECOIN?.RELATORIOS?.LUCRO_REMESSAS
              ],
              children: [
                { title: 'Remessas internacionais', to: '/reports/stablecoin/remittance', access: [this.$access?.STABLECOIN?.RELATORIOS?.REMESSAS] },
                { title: 'Lucro / Remessas internacionais', to: '/reports/stablecoin/management', access: [this.$access?.STABLECOIN?.RELATORIOS?.LUCRO_REMESSAS] }
              ]
            }
          ]
        },
        {
          label: 'CADASTROS',
          items: [
            {
              title: 'Corban',
              icon: 'PhBriefcase',
              access: [this.$access.MENU.CORBAN.CADASTROS, this.$access.MENU.CORBAN.COMISSOES],
              children: [
                { title: 'Cadastros', to: '/correspondents', access: [this.$access.MENU.CORBAN.CADASTROS] },
                { title: 'Comissões', to: '/operations/commissions', access: [this.$access.MENU.CORBAN.COMISSOES] }
              ]
            },
            {
              title: 'Cadastros operacionais',
              icon: 'PhPlusCircle',
              access: [
                this.$access.MENU.CADASTROS_OPERACIONAIS.NATUREZAS_OPERACAO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.BANCOS,
                this.$access.MENU.CADASTROS_OPERACIONAIS.SPREADS_MOEDAS,
                this.$access.MENU.CADASTROS_OPERACIONAIS.METODOS_PAGAMENTO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.APIS_COTACAO,
                this.$access.MENU.CADASTROS_OPERACIONAIS.TEMPLATES
              ],
              children: [
                { title: 'Naturezas de operação', to: '/registrations/operation-natures', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.NATUREZAS_OPERACAO] },
                { title: 'Bancos', to: '/registrations/banks', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.BANCOS] },
                { title: 'Spread / Moedas', to: '/registrations/currencies', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.SPREADS_MOEDAS] },
                { title: 'Métodos de pagamento', to: '/registrations/payment-methods', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.METODOS_PAGAMENTO] },
                { title: 'Apis de cotação', to: '/registrations/rate-apis', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.APIS_COTACAO] },
                { title: 'Templates', to: '/registrations/templates', access: [this.$access.MENU.CADASTROS_OPERACIONAIS.TEMPLATES] }
              ]
            }
          ]
        },
        {
          label: 'ADMINISTRAÇÃO',
          items: [
            { title: 'Usuários', icon: 'PhUser', to: '/users', access: [this.$access.MENU.USUARIOS] },
            { title: 'Perfis', icon: 'PhIdentificationCard', to: '/profiles', access: [this.$access.MENU.PERFIS] },
            { title: 'Auditoria', icon: 'PhShieldCheck', to: '/audit', access: [this.$access.MENU.AUDITORIA] }
          ]
        },
        {
          label: 'OUTROS',
          items: [
            { title: 'Gerar Invoices', icon: 'PhReceipt', to: null, access: [this.$access.MENU.GERAR_INVOICES] },
            { title: 'Fechamento mensal', icon: 'PhCalendarCheck', to: '/exchange-closing', access: [this.$access.MENU.RELATORIOS_OPERACIONAIS.CONTA_CORRENTE_BANCOS] }
          ]
        }
      ];
    },

    currentPasswordRules() {
      return [v => !!v || this.$t('menu.changePassword.currentPasswordRequired')];
    },

    newPasswordRules() {
      return [v => !!v || this.$t('menu.changePassword.newPasswordRequired')];
    },

    confirmPasswordRules() {
      return [
        v => !!v || this.$t('menu.changePassword.confirmPasswordRequired'),
        v => v === this.user.newPassword || this.$t('menu.changePassword.passwordMismatch')
      ];
    }
  },
  props: {
    isOpenSimulator: {
      type: Boolean,
      default: false
    }
  },
  model: {
    prop: 'isOpenSimulator',
    event: 'onChange'
  },

  methods: {
    async _initOnboardingState() {
      await this.loadAdminStatus();
      await this.loadChecklistStatus();
    },

    hasAccessToItem(accessCodes, item) {
      if (item?.adminOnly && !this.isAdminUser) return false;

      if (!accessCodes || accessCodes.length === 0) return true;

      const userAccess = this.$store.getters[OPTIONS_STORE_ACCESS.GETTERS.GET_ACCESS];

      if (!userAccess || userAccess.length === 0) return false;

      const matched = userAccess.filter((a) => accessCodes.includes(a.code));

      if (matched.length === 0) return false;

      const reduced = matched.reduce((acc, cur) => {
        acc.value = acc.value || ACCESS_VALUES.HIDDEN;

        if (acc.value === ACCESS_VALUES.ENABLED || acc.value === cur.value) return acc;

        if (cur.value === ACCESS_VALUES.ENABLED) acc.value = ACCESS_VALUES.ENABLED;

        if (cur.value === ACCESS_VALUES.DISABLED) acc.value = ACCESS_VALUES.DISABLED;

        return acc;
      }, {});

      return reduced.value === ACCESS_VALUES.ENABLED;
    },

    togglePin() {
      this.isPinned = !this.isPinned;
      localStorage.setItem('menu-pinned', this.isPinned);
    },
    openAvatarDialog() {
      this.isAvatarDialogOpen = true;
    },
    onAvatarError() {
      this.isAvatarError = true;
    },
    async loadAvatar() {
      if (!this.user?.id) return;

      try {
        const response = await this.userAvatarApi.getAvatarBlob(this.user.id);

        if (response && response.data) {
          this.avatarObjectUrl = URL.createObjectURL(response.data);
          this.isAvatarError = false;
        } else {
          this.avatarObjectUrl = null;
        }
      } catch {
        this.avatarObjectUrl = null;
      }
    },
    // --- WhatsApp unread count ---

    async loadWhatsAppUnreadCount() {
      if (!this.hasWhatsAppAccess) return;

      try {
        const response = await this.whatsAppChatApi.getUnreadCount();
        const data = response.data;
        this.whatsappUnreadCount = (data && data.unreadCount) || 0;
      } catch {
        this.whatsappUnreadCount = 0;
      }
    },

    saveWhatsAppUnreadCount() {
      // Noop — contagem agora vem do banco, nao do localStorage
    },

    onWhatsAppUnreadRefresh() {
      this.loadWhatsAppUnreadCount();
    },

    openOnboardingChecklist() {
      this.goToFirstSteps();
    },

    goToFirstSteps() {
      this.$router.push('/first-steps').catch(() => { // eslint-disable-next-line no-empty
      });
    },

    goToWhatsAppInbox() {
      this.$router.push('/whatsapp-inbox').catch(() => {});
    },

    openOperationSimulator() {
      this.$emit('onChange', true);
    },
    closeOperationSimulator() {
      this.$emit('onChange', false);
    },
    goToConfigurations() {
      this.$router.push({ name: 'Configuration' }).catch(() => {});
    },

    goToEmailLogs() {
      this.$router.push({ name: 'EmailLogsControl' }).catch(() => {});
    },

    goToWhatsAppLogs() {
      this.$router.push({ name: 'WhatsAppLogsControl' }).catch(() => {});
    },

    /* Com o chat embutido no ar, "Suporte" abre o painel; sem ele (instancia sem
       portal, recusa, rede fora), segue abrindo o portal em outra aba. */
    goToHelpDesk() {
      const isWidgetAvailable = this.$store.getters[OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.IS_AVAILABLE];

      if (isWidgetAvailable) this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.REQUEST_OPEN);
      else window.open(this.$router.resolve(SUPPORT_ENTRY_PATH).href, '_blank', 'noopener');

      mixpanel.track('click', {
        button_name: isWidgetAvailable ? SUPPORT_BUTTON_NAME.WIDGET : SUPPORT_BUTTON_NAME.PORTAL
      });
    },
    openReleasesModal() {
      this.isOpenReleasesModal = true;
    },
    async checkRecentReleases() {
      /* Sem icone, sem chamada: pedir as notas para quem nao as vera e uma
         requisicao por sessao sem destino. */
      if (!this.isPerfilAdministrativo) return;

      try {
        const response = await releasesApi.getReleases();
        const releases = response.data || [];

        let hasRecentRelease = false;

        if (releases.length > 0) {
          const latestRelease = releases[0];
          const releaseDate = new Date(latestRelease.launchDate);
          const now = new Date();
          const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          hasRecentRelease = releaseDate >= twoWeeksAgo;
        }

        this.$store.commit(OPTIONS_STORE_RELEASES.MUTATIONS.UPDATE_RELEASES_STATE, {
          showReleasesIcon: true,
          hasRecentRelease
        });
      } catch (error) {
        this.$store.commit(OPTIONS_STORE_RELEASES.MUTATIONS.UPDATE_RELEASES_STATE, {
          showReleasesIcon: false,
          hasRecentRelease: false
        });
      }
    },

    deferredLoadConfigurations() {
      const delayMs = 600;
      this._configurationsTimeoutId = setTimeout(() => {
        this.getAllConfigurationsForStore();
      }, delayMs);
    },
    getAllConfigurationsForStore() {
      this.configurationApi
        .findAll()
        .then((response) => {
          const configurations = response.data.map((c) => new ConfigurationModel(c));
          this.updateAllConfigurationsStoreBasedAPI(configurations);
        })
        .catch(() => {});
    },
    updateAllConfigurationsStoreBasedAPI(configurations) {
      this.$store.dispatch(
        OPTIONS_STORE_CONFIGURATIONS.ACTIONS.UPDATE_CONFIGURATION_MODEL,
        configurations
      );
    },
    changeTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      utilsStorage.setThemeSettings(this.$vuetify.theme.dark);
    },
    openIdentities() {
      this.identitiesWindow = true;
      mixpanel.track('click', {
        button_name: 'btn_identities'
      });
    },
    changePassword() {
      this.changePasswordWindow = true;
      mixpanel.track('click', {
        button_name: 'btn_change_password'
      });
    },
    exit() {
      mixpanel.track('click', {
        button_name: 'btn_logout'
      });
      this.logout();
    },

    logout() {
      this.$store.dispatch(OPTIONS_STORE_ACCESS.ACTIONS.CLEAR_ACCESS);
      utilsStorage.removeAuth();
      utilsStorage.clearReturnPath();
      this.$eventBus.$emit('token-changed');
      this.$router.push({ name: 'Login' });
    },
    startPendo() {
      if (!window.pendo) return;

      window.pendo.initialize({
        visitor: {
          id: this.user.id,
          email: this.user.email,
          full_name: this.user.name,
          role: this.user.profileId
        },
        account: {
          id: this.user.id
        }
      });
    },
    async updatePassword() {
      if (!this.$refs.formSenha.validate()) return;

      try {
        await authApi.changePassword(this.user.newPassword, this.user.currentPassword);
        this.sendMessage(this.$t('menu.changePassword.successMessage'), 'success');
        this.changePasswordWindow = false;
        this.cleanFields();
      } catch (error) {
        this.sendErrorMessage(error, this.$t('menu.changePassword.errorMessage'));
      }
    },

    close() {
      this.cleanFields();
      this.changePasswordWindow = false;
    },

    cleanFields() {
      this.$refs.formSenha?.resetValidation();
      this.user = new UserModel();
    }
  }
};
</script>

<style scoped>
.alignment-variables {
  margin-left: 15px;
}
.simulation-wrapper {
  display: flex;
  align-items: center;
}

.simulation-wrapper .v-btn .v-btn__content {
  display: flex;
  align-items: center;
}

.releases-icon-container {
  position: relative;
  display: inline-block;
}

.whatsapp-icon-container {
  position: relative;
  display: inline-block;
}

.whatsapp-notification-badge {
  position: absolute;
  top: -12px;
  right: -14px;
  background-color: #25D366;
  color: #ffffff;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: bold;
  border: 2px solid var(--v-backgroundPrimary-base);
  z-index: 10;
  padding: 0 4px;
  animation: pulse-whatsapp 2s infinite;
}

@keyframes pulse-whatsapp {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.6);
  }
  70% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}

.releases-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.releases-badge-dot {
  width: 12px;
  height: 12px;
  background-color: var(--v-success-base);
  border-radius: 50%;
  border: 2px solid var(--v-backgroundPrimary-base);
  box-shadow: none;
  animation: pulse-dot 2s infinite;
}

.onboarding-icon-container {
  position: relative;
  display: inline-block;
}

.onboarding-pulse-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 10px;
  height: 10px;
  background-color: var(--v-warning-base);
  border-radius: 50%;
  border: 2px solid var(--v-backgroundPrimary-base);
  animation: pulse-onboarding 2s infinite;
}

@keyframes pulse-onboarding {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 152, 0, 0.7);
  }
  70% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}

.btn-operation-simulator {
  margin-bottom: 3px;
}

@-webkit-keyframes pulse {
  0% {
    box-shadow: none;
  }
  70% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(255, 82, 82, 0.6);
  }
  70% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7), 0 0 0 0 rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: none;
  }
  100% {
    box-shadow: none;
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.position-relative {
  position: relative;
}

/* Avatar do usuário na top bar */
.user-avatar-btn {
  text-transform: none;
  letter-spacing: normal;
  height: auto;
}

.user-avatar-btn .v-avatar {
  cursor: pointer;
}

.user-info-label {
  flex-direction: column;
  align-items: flex-start;
  margin-left: 8px;
  max-width: 160px;
  line-height: 1.3;
}

.user-info-name {
  font-size: 0.8rem;
  font-weight: 600;
  max-width: 160px;
}

.user-info-email {
  font-size: 0.7rem;
  font-weight: 400;
  opacity: 0.7;
  max-width: 160px;
}

.customer-name-label {
  font-size: 0.85rem;
  font-weight: 500;
  max-width: 160px;
  color: rgba(0, 0, 0, 0.7);
}

.theme--dark .customer-name-label {
  color: rgba(255, 255, 255, 0.7);
}
/* Logo no topo da sidebar */
.sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  /* 68px, não 64: a faixa precisa ser a MESMA nos dois estados. Colapsada ela
     mostra o símbolo (28px) e o mínimo de 64 vencia; expandida mostra o logo
     completo (36px + 32 de padding = 68) e a faixa crescia 4px. Como ela é o
     primeiro bloco da sidebar, esses 4px empurravam o menu inteiro para baixo a
     cada abertura. */
  min-height: 68px;
}

.sidebar-logo-full {
  max-width: 160px;
  max-height: 36px;
  object-fit: contain;
}

.sidebar-logo-mini {
  display: none;
  max-height: 28px;
}

.sidebar-pin-btn {
  margin-left: auto;
}

.v-navigation-drawer--mini-variant .sidebar-pin-btn {
  display: none;
}

/* Mini-variant: mostrar logo símbolo, esconder logo completo */
.v-navigation-drawer--mini-variant .sidebar-logo-full {
  display: none;
}

.v-navigation-drawer--mini-variant .sidebar-logo-mini {
  display: block;
}

/* Remover borda direita padrão do Vuetify no navigation-drawer */
::v-deep .v-navigation-drawer__border {
  display: none;
}

::v-deep .v-navigation-drawer {
  transition: width 0.2s ease;
  z-index: 9;
}

/* App-bar no fluxo normal, sem position fixed */
.v-app-bar {
  position: relative;
  left: auto;
  z-index: 5;
  transition: margin-left 0.2s ease;
}

/* ---------------------------------------------------------------------------
   Barra superior
   Trilha à esquerda; busca e ações agrupadas à direita, separadas por um único
   respiro. Superfície clara com hairline embaixo — o contraste com o shell
   escuro do menu é o "ritmo claro/escuro" da marca.
--------------------------------------------------------------------------- */
/* Sem faixa e sem borda: o header vive sobre o fundo da página.
   O seletor é longo de propósito. `.tc-topbar` sozinho (mesmo com !important)
   perdia para as regras de superfície que o Vuetify e o legado aplicam sobre
   `.v-sheet`/`.v-app-bar`, e a faixa branca voltava. Encadear as classes que o
   próprio componente já tem sobe a especificidade sem inventar marcação. */
.v-application header.tc-topbar.v-app-bar.v-sheet {
  background: transparent !important;
  background-color: transparent !important;
  border-bottom: none !important;
  box-shadow: none !important;
}

.tc-topbar ::v-deep .v-toolbar__content {
  padding: 0 var(--tc-space-6) 0 var(--tc-space-5);
  gap: 0;
}

/* Ritmo de leitura: 4px entre irmãos do mesmo grupo, 12px + hairline entre
   grupos. É o espaçamento que informa a hierarquia, não uma moldura. */
.tc-topbar__group {
  display: flex;
  align-items: center;
  gap: var(--tc-space-1);
}

.tc-topbar__sep {
  width: 1px;
  height: 22px;
  flex: none;
  background: var(--tc-line);
  margin: 0 var(--tc-space-3);
}

/* Separador so vale se houver algo dos dois lados. Um grupo inteiro pode
   esvaziar por perfil — "Simular operacao" e da operacao interna —, e ai
   sobravam dois tracos colados entre o sino e o usuario.

   Esconde-se o de DEPOIS do grupo vazio: assim resta um separador quando o
   vazio esta no meio, e nenhum quando e o primeiro grupo que some. O `:empty`
   funciona porque o que o `v-if` deixa para tras e comentario, e comentario nao
   impede o seletor de casar. */
.tc-topbar__group:empty + .tc-topbar__sep {
  display: none;
}

/* No celular a barra fica com o mesmo conteúdo numa faixa de 360px, e a trilha
   de navegação é a única peça elástica: ela encolhia a 17px — some da tela — e
   os itens quebravam em três linhas, passando dos 64px da barra e empurrando o
   conteúdo para baixo.

   Três ajustes, nenhum deles escondendo função: os grupos da direita param de
   encolher (são ícones, encolher só os corta), o respiro entre grupos aperta, e
   a trilha fica com o que sobra, em uma linha. Ela também passa a mostrar só o
   item ATUAL: o caminho até aqui vale pouco numa faixa de 80px, e o nome do
   registro aberto vale muito. */
@media (max-width: 599px) {
  .tc-topbar ::v-deep .v-toolbar__content {
    padding: 0 var(--tc-space-3);
  }

  .tc-topbar__group,
  .tc-topbar__sep,
  .tc-topbar__user {
    flex: none;
  }

  .tc-topbar__sep {
    margin: 0 var(--tc-space-1);
  }

  .tc-topbar ::v-deep .breadcrumb-nav {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tc-topbar ::v-deep .breadcrumb-nav li:not(:last-child) {
    display: none;
  }
}

/* TUDO no header tem 36px de altura.
   Estavam três medidas ao mesmo tempo: ícones em 36, "Simular operação" em 30
   (pelo `small`) e o bloco do usuário em 44 (pelo padding). Numa fileira única
   a diferença lê como desalinho, não como hierarquia. */
.tc-topbar ::v-deep .v-btn,
.tc-topbar ::v-deep .v-chip,
.tc-topbar__user {
  height: 36px !important;
  min-height: 36px !important;
}

.tc-topbar ::v-deep .v-btn--icon {
  width: 36px !important;
}

/* Ícones limpos, sem moldura: num header sem faixa a caixa hairline em cada
   ícone traria de volta o peso que a faixa tinha. Ficam neutros e só ganham
   cor no hover.
   É o ESCOPO que aplica isso, não uma classe: ClaraLauncher e
   NotificationPanel renderizam o próprio <v-btn>, então marcar apenas os
   botões declarados aqui deixaria metade tratada e metade não.

   O `margin: 0` não é cosmético: esses componentes trazem `mr-2` por conta
   própria, o que somava 8px ao gap do grupo e quebrava o ritmo (saía 4px entre
   uns e 12px entre outros). Quem define o espaçamento é o grupo. */
.tc-topbar__group ::v-deep .v-btn--icon,
.tc-topbar__group ::v-deep .v-btn--fab {
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  border-radius: var(--tc-radius-sm) !important;
  border: none;
  background: transparent;
  margin: 0 !important;
  transition: background var(--tc-transition), color var(--tc-transition);
}

/* A Clara fica de fora da cor: ela tem identidade própria (o azul da marca dela)
   seletor (0,3,0 com !important) vencia o `color` do próprio componente
   (0,2,0), deixando o ícone cinza mesmo com o painel aberto. */
.tc-topbar__group ::v-deep .v-btn--icon:not(.clara-launcher),
.tc-topbar__group ::v-deep .v-btn--fab:not(.clara-launcher) {
  color: var(--tc-text-2) !important;
}

.tc-topbar__group ::v-deep .v-btn--icon:not(.clara-launcher):hover {
  background: var(--tc-action-tint);
  color: var(--tc-action) !important;
}

/* O preenchimento da Clara mora AQUI, e não no próprio componente, porque a
   regra concorrente é a de cima: `.v-btn--icon { background: transparent }`
   neste mesmo escopo. Declarar do lado do componente vira disputa de cascata —
   o anel da marca passava, o fundo não. Na mesma camada não há disputa. */
.tc-topbar__group ::v-deep .v-btn--icon.clara-launcher:hover,
.tc-topbar__group ::v-deep .v-btn--icon.clara-launcher--on {
  background: var(--clara-tint);
}

/* O aviso de pré-venda é status, não ação: vira um selo tonal de alerta em vez
   de texto solto competindo com os botões ao lado. */
.tc-topbar__group ::v-deep .pre-sale-notification-content {
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 var(--tc-space-3);
  border-radius: var(--tc-radius-sm);
  background: var(--tc-warning-soft);
  color: var(--tc-warning);
  font-family: var(--tc-font-body);
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  white-space: nowrap;
}

/* Gatilho do menu do usuário: alvo único, não avatar + engrenagem soltos. */
.tc-topbar__user {
  display: flex;
  align-items: center;
  gap: var(--tc-space-2);
  padding: 0 var(--tc-space-2) 0 3px;
  border: 1px solid transparent;
  border-radius: var(--tc-radius-sm);
  background: transparent;
  cursor: pointer;
  transition: background var(--tc-transition), border-color var(--tc-transition);
}

.tc-topbar__user:hover {
  background: var(--tc-action-tint);
  border-color: var(--tc-line);
}

.tc-topbar__avatar {
  background: var(--tc-action-tint-strong);
  border: 1px solid var(--tc-line);
  color: var(--tc-action);
}

.tc-topbar__initials {
  color: var(--tc-action);
  font-family: var(--tc-font-display);
  font-weight: var(--tc-fw-semibold);
  font-size: 11px;
}

.tc-topbar__identity {
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
  max-width: 168px;
  min-width: 0;
}

.tc-topbar__name {
  font-family: var(--tc-font-body);
  font-size: var(--tc-fs-sm);
  font-weight: var(--tc-fw-medium);
  color: var(--tc-text);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mono maiúsculo: o e-mail é rótulo de apoio, não texto de leitura — o mesmo
   papel que o rótulo tem na tabela e no card. */
.tc-topbar__mail {
  font-family: var(--tc-font-mono);
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--tc-text-3);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tc-topbar__caret {
  color: var(--tc-text-3);
  flex: none;
}

/* Menu lateral - padding e arredondamento */
::v-deep .menu-static-list {
  padding: 8px;
}

/* Sem respiro lateral aqui: no trilho colapsado quem centraliza o item é a
   margem automática dele (ver design-system/styles/vuetify.css §9.1), e um
   padding aqui desloca o quadrado do centro.

   O vertical, porém, acompanha o expandido (8px): só o horizontal muda entre os
   dois estados. Com 4px aqui, o primeiro item subia 4px ao colapsar. */
::v-deep .v-navigation-drawer--mini-variant .menu-static-list {
  padding: 8px 0;
}

::v-deep .menu-static-list .v-list-item {
  border-radius: var(--tc-radius-sm);
  margin-bottom: 2px;
}

/* Mesma altura do item simples. O Vuetify dá 40px ao cabeçalho de grupo em
   lista `dense`, enquanto o trilho colapsado força 44 — a diferença de 4px por
   grupo se acumulava e deslocava tudo que vem abaixo deles ao abrir o menu. */
::v-deep .menu-static-list .v-list-group__header {
  border-radius: var(--tc-radius-sm);
  min-height: 44px;
}

/* Estado ativo do menu — cor e fundo vêm dos tokens de shell do DS
   (design-system/styles/vuetify.css §9.1), que já entregam a barra de acento,
   o fundo suave e o peso 600. Aqui ficam só os ajustes que dependem da
   estrutura interna do Vuetify.

   As variantes claras que existiam aqui viraram código morto quando o shell
   passou a ser escuro nos dois temas; e as escuras pintavam o item ativo com
   --v-primary-lighten2, um roxo que não é da paleta. */

/* O ::before é o overlay do Vuetify; zerado para o fundo do DS aparecer. */
::v-deep .menu-static-list .v-list-item--active::before,
::v-deep .menu-static-list .v-list-item:not(.v-list-item--active):hover::before {
  opacity: 0;
}

::v-deep .menu-static-list .v-list-item--active .v-list-item__title {
  color: var(--tc-shell-text-active);
  font-weight: var(--tc-fw-semibold);
}

::v-deep .menu-static-list .v-list-item--active .v-list-item__action .v-icon,
::v-deep .menu-static-list .v-list-item--active .v-list-item__action svg {
  color: var(--tc-shell-accent);
}

::v-deep .menu-static-list .v-list-item:not(.v-list-item--active):hover {
  background-color: var(--tc-shell-hover-bg);
}
</style>
